import { makeAutoObservable, reaction, type IReactionDisposer } from 'mobx'
import { nanoid } from 'nanoid'
import { showToast } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { TableStore } from 'shared/ui/TableNew'
import {
  type IResponseCreatePersonalAccessToken,
  type IResponsePersonalAccessToken,
} from 'entities/Auth'
import { DeveloperAuthApi } from 'entities/Auth/api'
import { PersonalAccessTokenRow } from 'pages/settings/pages/developer/pages/accessTokens/model/PersonalAccessTokenRow'
import { NewTokenModalActions } from 'pages/settings/pages/developer/pages/accessTokens/ui/NewTokenModal/NewTokenModalActions'
import { NewTokenModalContent } from 'pages/settings/pages/developer/pages/accessTokens/ui/NewTokenModal/NewTokenModalContent'

export class AccessTokensStore {
  private _loading = false
  private _tokens: IResponsePersonalAccessToken[] = []
  private readonly _tableStore: TableStore<PersonalAccessTokenRow>
  private readonly _listMap = new Map<string, PersonalAccessTokenRow>()
  private readonly _newTokenModalId = nanoid()
  private _newTokenName = ''
  private _newTokenNameError = ''
  private _newTokenModalSaving = false
  private _createTokenError = false
  private _createdTokenResponse: IResponseCreatePersonalAccessToken | null = null
  private readonly _revokeModalId = nanoid()
  private _revokingToken: IResponsePersonalAccessToken | null = null
  private _revoking = false
  private _revokeModalDisposeReaction: IReactionDisposer | null = null

  constructor() {
    makeAutoObservable(this)
    this._tableStore = new TableStore<PersonalAccessTokenRow>({
      key: 'developer_access_tokens',
    })
  }

  init = async () => {
    await this.loadTokens()
  }

  loadTokens = async () => {
    this._loading = true

    try {
      const { data } = await DeveloperAuthApi.getPersonalAccessTokens()
      const list = data ?? []
      this._tokens = list
      this._listMap.clear()
      const rows = list.map((item) => new PersonalAccessTokenRow(item))
      rows.forEach((row) => this._listMap.set(String(row.id), row))
      this._tableStore.setRows(rows)
      this._tableStore.setTotal(rows.length)
    } finally {
      this._loading = false
    }
  }

  get loading() {
    return this._loading
  }

  get tokens() {
    return this._tokens
  }

  get tableStore() {
    return this._tableStore
  }

  get items() {
    return Array.from(this._listMap.values())
  }

  get newTokenName() {
    return this._newTokenName
  }

  get newTokenNameError() {
    return this._newTokenNameError
  }

  get newTokenModalSaving() {
    return this._newTokenModalSaving
  }

  get createTokenError() {
    return this._createTokenError
  }

  clearCreateTokenError = () => {
    this._createTokenError = false
  }

  get isNewTokenFormValid(): boolean {
    return Boolean(this._newTokenName.trim())
  }

  get createdTokenResponse() {
    return this._createdTokenResponse
  }

  setNewTokenName = (value: string) => {
    this._newTokenName = value

    if (value.trim()) {
      this._newTokenNameError = ''
    }
  }

  validateNewTokenNameBlur = () => {
    if (!this._newTokenName.trim()) {
      this._newTokenNameError = 'Please enter a token name'
    } else {
      this._newTokenNameError = ''
    }
  }

  openNewTokenModal = () => {
    this._newTokenName = ''
    this._newTokenNameError = ''
    this._createTokenError = false
    this._createdTokenResponse = null

    modalStore.addModal({
      id: this._newTokenModalId,
      type: ModalTypeList.DEFAULT,
      width: 480,
      title: 'New token',
      showCloseButton: true,
      closeText: 'Cancel',
      ModalContent: NewTokenModalContent,
      ModalContentProps: { store: this },
      ModalActions: NewTokenModalActions,
      secondaryAction: {
        text: 'Cancel',
        onAction: () => this.closeNewTokenModal(),
      },
      onClose: () => this.closeNewTokenModal(),
    })
  }

  closeNewTokenModal = () => {
    modalStore.removeModal(this._newTokenModalId)
    this._newTokenName = ''
    this._newTokenNameError = ''
    this._createTokenError = false
    this._createdTokenResponse = null
  }

  openRevokeModal = (token: IResponsePersonalAccessToken) => {
    this._revokingToken = token
    this._revoking = false
    modalStore.addModal({
      id: this._revokeModalId,
      type: ModalTypeList.ALERT,
      width: 480,
      title: 'Revoke token?',
      desc: 'This action cannot be undone',
      showCloseButton: true,
      closeText: 'Cancel',
      primaryAction: {
        text: 'Yes, revoke',
        disabled: false,
        onAction: () => this.handleRevokeToken(),
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => this.closeRevokeModal(),
      },
      onClose: () => this.closeRevokeModal(),
    })
    this._revokeModalDisposeReaction?.()
    this._revokeModalDisposeReaction = reaction(
      () => this._revoking,
      (revoking) => {
        const modal = modalStore.getModal(this._revokeModalId)
        if (modal && 'primaryAction' in modal && modal.primaryAction) {
          modalStore.removeModal(this._revokeModalId)
          modalStore.addModal({
            ...modal,
            primaryAction: {
              ...modal.primaryAction,
              disabled: revoking,
            },
          })
        }
      }
    )
  }

  closeRevokeModal = () => {
    this._revokeModalDisposeReaction?.()
    this._revokeModalDisposeReaction = null
    modalStore.removeModal(this._revokeModalId)
    this._revokingToken = null
    this._revoking = false
  }

  handleRevokeToken = async () => {
    const token = this._revokingToken
    if (!token) return

    this._revoking = true
    try {
      await DeveloperAuthApi.revokePersonalAccessToken(token.id)
      this.closeRevokeModal()
      showToast({ type: 'success', title: 'Token revoked' })
      this._tokens = this._tokens.filter((t) => t.id !== token.id)
      this._listMap.delete(token.id)
      const rows = Array.from(this._listMap.values())
      this._tableStore.setRows(rows)
      this._tableStore.setTotal(rows.length)
    } catch {
      showToast({ type: 'error', title: 'Failed to revoke token' })
    } finally {
      this._revoking = false
    }
  }

  handleCreateToken = async () => {
    const name = this._newTokenName.trim()
    if (!name) return

    this._createTokenError = false
    this._newTokenModalSaving = true
    try {
      const { data } = await DeveloperAuthApi.createPersonalAccessToken({ name })
      this._newTokenModalSaving = false
      if (!data) return
      this._createdTokenResponse = data
      modalStore.removeModal(this._newTokenModalId)
      modalStore.addModal({
        id: this._newTokenModalId,
        type: ModalTypeList.DEFAULT,
        width: 480,
        title: 'New token',
        showCloseButton: false,
        disabledOverlayClose: true,
        ModalContent: NewTokenModalContent,
        ModalContentProps: { store: this },
      })
      await this.loadTokens()
    } catch {
      this._createTokenError = true
    } finally {
      this._newTokenModalSaving = false
    }
  }

  dispose = () => {
    this._revokeModalDisposeReaction?.()
    this._revokeModalDisposeReaction = null
    modalStore.removeModal(this._revokeModalId)
    modalStore.removeModal(this._newTokenModalId)
  }
}
